import { useState, useRef, useEffect } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

function Contact() {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const startTimeRef = useRef(Date.now());
  const interactionCountRef = useRef(0);
  const { executeRecaptcha } = useGoogleReCaptcha();

  useEffect(() => {
    // Track user interactions
    const handleInteraction = () => {
      interactionCountRef.current += 1;
    };

    window.addEventListener('mousemove', handleInteraction);
    window.addEventListener('keypress', handleInteraction);

    return () => {
      window.removeEventListener('mousemove', handleInteraction);
      window.removeEventListener('keypress', handleInteraction);
    };
  }, []);

  const getDeviceInfo = () => {
    const ua = navigator.userAgent;
    return {
      userAgent: ua,
      platform: navigator.platform,
      language: navigator.language,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      cookiesEnabled: navigator.cookieEnabled,
      onlineStatus: navigator.onLine,
      referrer: document.referrer || 'direct',
      // Browser detection
      browser: (() => {
        if (ua.includes('Firefox')) return 'Firefox';
        if (ua.includes('Chrome')) return 'Chrome';
        if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
        if (ua.includes('Edge')) return 'Edge';
        if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
        return 'Unknown';
      })(),
      // Device type detection
      deviceType: (() => {
        if (/Mobile|Android|iPhone|iPad|iPod/.test(ua)) return 'Mobile';
        if (/Tablet|iPad/.test(ua)) return 'Tablet';
        return 'Desktop';
      })(),
      // OS detection
      os: (() => {
        if (ua.includes('Windows')) return 'Windows';
        if (ua.includes('Mac')) return 'macOS';
        if (ua.includes('Linux')) return 'Linux';
        if (ua.includes('Android')) return 'Android';
        if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
        return 'Unknown';
      })(),
    };
  };

  const validateSubmission = () => {
    const errors = [];

    // 1. Honeypot check - bots often fill hidden fields
    if (honeypot) {
      errors.push('Bot detected: honeypot filled');
      return { valid: false, errors };
    }

    // 2. Time-based check - form filled too quickly (less than 3 seconds)
    const timeSpent = (Date.now() - startTimeRef.current) / 1000;
    if (timeSpent < 3) {
      errors.push('Bot detected: form submitted too quickly');
      return { valid: false, errors };
    }

    // 3. Interaction check - no mouse/keyboard activity
    if (interactionCountRef.current < 5) {
      errors.push('Bot detected: insufficient user interaction');
      return { valid: false, errors };
    }

    // 4. Content validation - spam patterns
    const spamPatterns = [
      /\b(viagra|cialis|loan|casino|poker)\b/i,
      /(https?:\/\/.*){3,}/i, // Multiple URLs
      /\b\w+\.(ru|cn)\b/i, // Suspicious TLDs
      /<script|<iframe|javascript:/i, // Script injection attempts
    ];

    const allText = `${form.name} ${form.email} ${form.message}`;
    for (const pattern of spamPatterns) {
      if (pattern.test(allText)) {
        errors.push('Spam content detected');
        return { valid: false, errors };
      }
    }

    // 5. Email validation - basic but catches many bots
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(form.email)) {
      errors.push('Invalid email format');
      return { valid: false, errors };
    }

    // 6. Length checks
    if (form.message.length < 10) {
      errors.push('Message too short');
      return { valid: false, errors };
    }

    if (form.message.length > 5000) {
      errors.push('Message too long');
      return { valid: false, errors };
    }

    return { valid: true, errors: [] };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      // Validate submission
      const validation = validateSubmission();
      if (!validation.valid) {
        console.warn('Validation failed:', validation.errors);
        setStatus({
          type: 'error',
          message: 'Please fill out the form correctly.',
        });
        setIsSubmitting(false);
        return;
      }

      // Get reCAPTCHA token
      let recaptchaToken = null;
      if (executeRecaptcha) {
        recaptchaToken = await executeRecaptcha('contact_form');
      }

      // Save to Firestore with security metadata
      await addDoc(collection(db, 'contacts'), {
        name: form.name,
        email: form.email,
        message: form.message,
        createdAt: serverTimestamp(),
        deviceInfo: getDeviceInfo(),
        source: 'website_contact_form',
        securityChecks: {
          recaptchaToken,
          timeSpent: (Date.now() - startTimeRef.current) / 1000,
          interactionCount: interactionCountRef.current,
          timestamp: new Date().toISOString(),
        },
      });

      // Also send email notification
      const subject = encodeURIComponent('Website Contact Form Submission');
      const body = encodeURIComponent(
        `Name: ${form.name}\nEmail: ${form.email}\n\nMessage:\n${form.message}`
      );
      window.location.href = `mailto:contact@joinbtlf.org?subject=${subject}&body=${body}`;

      setStatus({
        type: 'success',
        message: '',
        details: {
          name: form.name,
          email: form.email,
        },
      });
      setForm({ name: '', email: '', message: '' });

      // Reset for next submission
      startTimeRef.current = Date.now();
      interactionCountRef.current = 0;
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setStatus({
        type: 'error',
        message: 'Sorry, there was an error sending your message. Please try again or email us directly.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="contact" id="contact">
      <div className="container">
        <div className="section-header centered">
          <h2 className="section-title">Get in Touch</h2>
          <p className="section-subtitle">
            Questions? Want to get involved? We'd love to hear from you.
          </p>
        </div>
        <div className="contact-content">
          <div className="contact-info">
            <div className="contact-item">
              <i className="fas fa-envelope"></i>
              <div>
                <h4>Email</h4>
                <a href="mailto:contact@joinbtlf.org">contact@joinbtlf.org</a>
              </div>
            </div>
            <div className="contact-item">
              <i className="fas fa-map-marker-alt"></i>
              <div>
                <h4>Location</h4>
                <p>Dallas-Fort Worth, Texas</p>
              </div>
            </div>
            <div className="contact-item">
              <i className="fas fa-globe"></i>
              <div>
                <h4>Service Areas</h4>
                <p>Dallas, Texas &bull; Ilam District, Nepal</p>
              </div>
            </div>
          </div>
          <div className="contact-form">
            <form onSubmit={handleSubmit}>
              {status.type === 'success' && status.details && (
                <div className="form-status success">
                  <div className="success-icon">
                    <i className="fas fa-check-circle"></i>
                  </div>
                  <div className="success-content">
                    <h4>Message Sent Successfully!</h4>
                    <p>
                      Thank you, <strong>{status.details.name}</strong>! We've received your message.
                    </p>
                    <div className="success-details">
                      <div className="detail-item">
                        <i className="fas fa-envelope"></i>
                        <span>Confirmation sent to: <strong>{status.details.email}</strong></span>
                      </div>
                      <div className="detail-item">
                        <i className="fas fa-clock"></i>
                        <span>We typically respond within 24-48 hours</span>
                      </div>
                      <div className="detail-item">
                        <i className="fas fa-inbox"></i>
                        <span>Check your email (including spam folder) for our reply</span>
                      </div>
                    </div>
                    <p className="success-note">
                      If you don't hear from us within 2 business days, please email us directly at{' '}
                      <a href="mailto:contact@joinbtlf.org">contact@joinbtlf.org</a>
                    </p>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setStatus({ type: '', message: '', details: null });
                        startTimeRef.current = Date.now();
                        interactionCountRef.current = 0;
                      }}
                      style={{ marginTop: '1rem' }}
                    >
                      Send Another Message
                    </button>
                  </div>
                </div>
              )}

              {status.type === 'error' && status.message && (
                <div className="form-status error">
                  <div className="error-icon">
                    <i className="fas fa-exclamation-circle"></i>
                  </div>
                  <div className="error-content">
                    <h4>Oops! Something went wrong</h4>
                    <p>{status.message}</p>
                    <p className="error-note">
                      You can also reach us directly at{' '}
                      <a href="mailto:contact@joinbtlf.org">contact@joinbtlf.org</a>
                    </p>
                  </div>
                </div>
              )}

              {/* Only show form if not successfully submitted */}
              {status.type !== 'success' && (
                <>
                  {/* Honeypot field - hidden from users, bots will fill it */}
                  <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
                    <label htmlFor="website">Website</label>
                    <input
                      type="text"
                      id="website"
                      name="website"
                      tabIndex="-1"
                      autoComplete="off"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="name">Name</label>
                    <input
                      type="text"
                      id="name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      required
                      disabled={isSubmitting}
                      minLength="2"
                      maxLength="100"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="message">Message</label>
                    <textarea
                      id="message"
                      rows="5"
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      required
                      disabled={isSubmitting}
                      minLength="10"
                      maxLength="5000"
                    ></textarea>
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '1rem' }}>
                    This site is protected by reCAPTCHA and the Google{' '}
                    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                    </a>{' '}
                    and{' '}
                    <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
                      Terms of Service
                    </a>{' '}
                    apply.
                  </p>
                </>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Contact;
